import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { constantCase } from 'constant-case';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take, concatMap } from 'rxjs/operators';

const MAX_CONTACT_PERSONS = 5;

import { AccountPasswordModalComponent }
  from 'app/account-profile/account-password-modal/account-password-modal.component';
import { AnsweredRfpPreviewModalComponent } from
  'app/offer/offer-preview/answered-rfp-preview-modal/answered-rfp-preview-modal/answered-rfp-preview-modal.component';
import {
  OfferPreviewSendModalComponent
} from 'app/offer/offer-preview/offer-preview-send-modal/offer-preview-send-modal.component';
import {
  OfferPreviewSettingsModalComponent
} from 'app/offer/offer-preview/offer-preview-settings-modal/offer-preview-settings-modal.component';
import { RfpModalComponent } from 'app/offer/rfp-modal/rfp-modal.component';
import { MeasurementCalculationsModalComponent }
  from 'app/product-configuration/section/measurement-calculations-modal/measurement-calculations-modal.component';
import { Branch, Customer } from 'graphql/models/generated';
import {
  ModalContentType,
  Modal,
  ModalCheckboxFunction,
  ModalGroup,
  ModalStep,
  ModalFooterLinkFunction,
  ModalStepInputType
} from 'models/multimodal.model';
import { OfferBase } from 'models/offer.model';
import { AddFavoriteModalComponent } from 'shared/forms/add-favorite/add-favorite-modal/add-favorite-modal.component';
import {
  AddArticleToOfferModalComponent
} from 'shared/forms/article/add-article-to-offer-modal/add-article-to-offer-modal.component';
import { ArticleModalComponent } from 'shared/forms/article/article-modal/article-modal.component';
import { BranchBasicInfoModalComponent } from 'shared/forms/branch/basic-info-modal/basic-info-modal.component';
import {
  BranchInviteMemberModalComponent
} from 'shared/forms/branch/invite-member-modal/invite-member-modal.component';
import {
  ContactPersonBasicInfoModalComponent
} from 'shared/forms/contact-person/basic-info-modal/basic-info-modal.component';
import { ContactPersonSearchComponent } from 'shared/forms/contact-person/search/search.component';
import { CustomerBasicInfoModalComponent } from 'shared/forms/customer/basic-info-modal/basic-info-modal.component';
import { CustomerConditionsModalComponent } from 'shared/forms/customer/conditions-modal/conditions-modal.component';
import {
  CustomerCustomConditionsModalComponent
} from 'shared/forms/customer/custom-conditions-modal/custom-conditions-modal.component';
import { CustomerSearchComponent } from 'shared/forms/customer/search/search.component';
import { SearchCustomersWithoutConditionComponent }
  from 'shared/forms/customer/search-customers-without-condition/search-customers-without-condition.component';
import {
  EditFavoriteModalComponent
} from 'shared/forms/edit-favorite/edit-favorite-modal/edit-favorite-modal.component';
import {
  ExpirationDateModalComponent
} from 'shared/forms/expiration-date/expiration-date-modal/expiration-date-modal.component';
import {
  AddLooseArticleToOfferModalComponent
} from 'shared/forms/loose-categories/add-loose-article-to-offer-modal/add-loose-article-to-offer-modal.component';
import { LooseCategoriesModalComponent }
  from 'shared/forms/loose-categories/loose-categories-modal/loose-categories-modal.component';
import { ObjektBasicInfoModalComponent } from 'shared/forms/objekt/basic-info-modal/basic-info-modal.component';
import { ObjektSearchComponent } from 'shared/forms/objekt/search/search.component';
import { StoreService } from 'shared/services/store.service';
import { SuccessModalComponent } from 'shared/success-modal/success-modal.component';

import {
  CloseOfferModalComponent
} from '../../offer/calculation/summary/close-offer-modal/close-offer-modal.component';
import { DeleteAccountModalComponent } from 'shared/forms/branch/delete-account-modal/delete-account-modal.component';
import {
  EditArticleInOfferModalComponent
} from 'shared/forms/article/edit-article-in-offer-modal/edit-article-in-offer-modal.component';
import {
  BranchSftpSettingsModalComponent
} from 'app/settings/branch/sftp-settings/sftp-settings-modal/sftp-settings-modal.component'

@Injectable({
  providedIn: 'root'
})
export class MultimodalConfiguratorService {
  constructor(
    private storeService: StoreService,
    private translateService: TranslateService
  ) { }

  private getSteps(modalId: string, initialModalGroup: ModalGroup | undefined): ModalStep[] {
    const customerListStep: ModalStep = {
      id: modalId,
      body: { componentType: CustomerSearchComponent },
      footer: {
        link: {
          text: 'MODALS.CUSTOMER_LIST.FOOTER_LINK',
          modalId: this.getModalId('customer', 'form')
        }
      }
    };

    const contactPersonListStep: ModalStep = {
      id: modalId,
      header: {
        link: { text: 'MODALS.CONTACT_PERSON_LIST.HEADER_LINK' }
      },
      body: { componentType: ContactPersonSearchComponent },
      footer: {
        checkbox: {
          text: 'MODALS.CONTACT_PERSON_CREATE.FOOTER_CHECKBOX',
          function: ModalCheckboxFunction.OPEN_NEXT_MODAL,
          modalId: this.getModalId('objekt', 'list')
        },
        link: {
          text: 'MODALS.CONTACT_PERSON_LIST.FOOTER_LINK',
          modalId: this.getModalId('contactPerson', 'form')
        }
      }
    };

    const customerCustomConditionsFormStep: ModalStep = {
      id: modalId,
      header: { title: '{customer}' },
      body: { componentType: CustomerCustomConditionsModalComponent }
    };

    switch (modalId) {
      case 'closedOffer-form':
        return [
          {
            id: `${modalId}-s1`,
            class: 'modal-sm',
            header: { modalTitleOverride: 'MODALS.CONFIRM.CLOSE_OFFER' },
            body: {
              componentType: CloseOfferModalComponent,
              resetMinHeight: true
            },
            footer: {
              saveBtnName: 'GENERAL.YES',
              showCancelBtn: true
            }
          },
          {
            id: `${modalId}-s2`,
            class: ' ',
            header: { modalTitleOverride: 'MODALS.CLOSE_OFFER.HEADER_TITLE' },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.CLOSE_OFFER.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.GO_TO_DASHBOARD
              }
            }
          }
        ];
      case 'answeredRFPsPreview-form':
        return [{
          id: modalId,
          body: { componentType: AnsweredRfpPreviewModalComponent },
          footer: { hideFooter: true }
        }];
      case 'objekt-list':
        return [{
          id: modalId,
          body: { componentType: ObjektSearchComponent },
          footer: {
            checkbox: {
              text: 'MODALS.OBJEKT_CREATE.FOOTER_CHECKBOX',
              function: ModalCheckboxFunction.OPEN_NEXT_MODAL,
              modalId: this.getModalId('customerAll', 'list')
            },
            link: {
              text: 'MODALS.OBJEKT_LIST.FOOTER_LINK',
              modalId: this.getModalId('objekt', 'form')
            }
          }
        }];
      case 'objekt-form':
        return [{
          id: modalId,
          body: { componentType: ObjektBasicInfoModalComponent },
          footer: {
            checkbox: {
              text: 'MODALS.OBJEKT_CREATE.FOOTER_CHECKBOX',
              function: ModalCheckboxFunction.OPEN_NEXT_MODAL,
              modalId: this.getModalId('customerAll', 'list')
            },
            link: {
              text: 'MODALS.OBJEKT_CREATE.FOOTER_LINK',
              modalId: this.getModalId('objekt', 'list')
            }
          }
        }];
      case 'customer-list':
        return [customerListStep];
      case 'customer-form':
        return [
          {
            id: `${modalId}-s1`,
            header: {
              breadcrumb: {
                text: 'MODALS.CUSTOMER_CREATE.BASIC_INFO.STEP_TITLE'
              }
            },
            body: { componentType: CustomerBasicInfoModalComponent },
            footer: {
              checkbox: {
                text: 'MODALS.CUSTOMER_CREATE.BASIC_INFO.FOOTER_CHECKBOX',
                function: ModalCheckboxFunction.SKIP_NEXT_STEP
              },
              link: {
                text: 'MODALS.CUSTOMER_CREATE.BASIC_INFO.FOOTER_LINK',
                modalId: this.getModalId(
                  (initialModalGroup === 'customer') ? 'customer' : 'customerAll',
                  'list'
                )
              }
            }
          },
          {
            id: `${modalId}-s2`,
            header: {
              breadcrumb: {
                text: 'MODALS.CUSTOMER_CREATE.CONDITIONS.STEP_TITLE'
              }
            },
            body: { componentType: CustomerConditionsModalComponent }
          },
          {
            id: `${modalId}-s3`,
            header: {
              breadcrumb: {
                text: 'MODALS.CUSTOMER_CREATE.CONTACT_PERSON.STEP_TITLE'
              }
            },
            body: { componentType: ContactPersonBasicInfoModalComponent },
            footer: {
              checkbox: {
                text: 'MODALS.CUSTOMER_CREATE.CONTACT_PERSON.FOOTER_CHECKBOX',
                function: ModalCheckboxFunction.OPEN_NEXT_MODAL,
                modalId: this.getModalId('objekt', 'list')
              }
            }
          }];
      case 'contactPerson-list':
        return [contactPersonListStep];
      case 'contactPerson-form':
        return [
          {
            id: modalId,
            body: { componentType: ContactPersonBasicInfoModalComponent },
            footer: {
              checkbox: {
                text: 'MODALS.CONTACT_PERSON_CREATE.FOOTER_CHECKBOX',
                function: ModalCheckboxFunction.OPEN_NEXT_MODAL,
                modalId: this.getModalId('objekt', 'list')
              },
              link: {
                text: 'MODALS.CONTACT_PERSON_CREATE.FOOTER_LINK',
                modalId: this.getModalId('contactPerson', 'list')
              }
            }
          }
        ];
      case 'contactPerson-formUpdate':
        return [
          {
            id: modalId,
            body: { componentType: ContactPersonBasicInfoModalComponent }
          }
        ];
      case 'customerAll-list':
        return [
          { ...customerListStep, id: `${customerListStep.id}-s1` },
          { ...contactPersonListStep, id: `${contactPersonListStep.id}-s2` }
        ];
      case 'individualCondition-form':
        return [customerCustomConditionsFormStep];
      case 'newCondition-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: SearchCustomersWithoutConditionComponent }
          },
          { ...customerCustomConditionsFormStep, id: `${modalId}-s2` }
        ];
      case 'freeArticle-form':
      case 'freeArticle-formUpdate':
      case 'article-form':
      case 'article-formUpdate':
        return [
          {
            id: modalId,
            body: { componentType: ArticleModalComponent }
          }
        ];
      case 'addArticleToOffer-form':
        return [
          {
            id: modalId,
            body: { componentType: AddArticleToOfferModalComponent },
            footer: {
              saveBtnName: 'MODALS.ADD_ARTICLE_TO_OFFER.SAVE_BUTTON'
            }
          }
        ];
      case 'addFreeArticleToOffer-form':
        return [
          {
            id: modalId,
            body: { componentType: AddArticleToOfferModalComponent },
            footer: {
              saveBtnName: 'MODALS.ADD_ARTICLE_TO_OFFER.SAVE_BUTTON'
            }
          }
        ];
      case 'addFavorite-form':
        return [
          {
            id: modalId,
            body: { componentType: AddFavoriteModalComponent },
            footer: {
              saveBtnName: 'MODALS.ADD_FAVORITE.SAVE_BUTTON',
              showCancelBtn: true
            }
          }
        ];
      case 'editFavorite-form':
        return [
          {
            id: modalId,
            body: { componentType: EditFavoriteModalComponent },
            footer: {
              saveBtnName: 'MODALS.EDIT_FAVORITE.SAVE_BUTTON',
              showCancelBtn: true
            }
          }
        ];
      case 'expirationDate-form':
        return [
          {
            id: modalId,
            body: { componentType: ExpirationDateModalComponent },
            footer: {
              link: {
                text: 'MODALS.EXPIRATION_DATE.FOOTER_LINK',
                function: ModalFooterLinkFunction.RESET_FORM
              }
            }
          }
        ];
      case 'password-form':
        return [
          {
            id: modalId,
            body: { componentType: AccountPasswordModalComponent }
          }
        ];
      case 'send-success-form':
        return [
          {
            id: modalId,
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.CLOSE_OFFER.SAVE_BUTTON',
            }
          }
        ];
      case 'branch-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: BranchBasicInfoModalComponent }
          },
          {
            id: `${modalId}-s2`,
            header: {
              modalTitleOverride: 'MODALS.BRANCH_INVITE.HEADER_TITLE',
              image: 'branch-created'
            },
            body: { componentType: BranchInviteMemberModalComponent },
            footer: {
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          },
          {
            id: `${modalId}-s3`,
            header: { modalTitleOverride: 'MODALS.INVITE_MEMBER_SUCCESS.HEADER_TITLE' },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.INVITE_MEMBER_SUCCESS.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }

        ];
      case 'offerPreview-form':
        return [
          {
            id: `${modalId}-s1`,
            header: {
              breadcrumb: {
                text: 'MODALS.OFFER_PREVIEW.SETTINGS.STEP_TITLE'
              }
            },
            body: { componentType: OfferPreviewSettingsModalComponent },
            footer: {
              saveBtnName: 'MODALS.OFFER_PREVIEW.SETTINGS.SAVE_BUTTON'
            }
          },
          {
            id: `${modalId}-s2`,
            header: {
              breadcrumb: {
                text: 'MODALS.OFFER_PREVIEW.SEND.STEP_TITLE'
              }
            },
            body: { componentType: OfferPreviewSendModalComponent },
            footer: {
              saveBtnName: 'MODALS.OFFER_PREVIEW.SEND.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          },
          {
            id: `${modalId}-s3`,
            class: ' ', // reset modal-lg class on last step
            header: {
              modalTitleOverride: 'MODALS.OFFER_PREVIEW.SUCCESS.TITLE'
            },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.OFFER_PREVIEW.SUCCESS.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'rfp-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: RfpModalComponent },
            footer: {
              saveBtnName: 'MODALS.RFP.SAVE_BUTTON'
            }
          },
          {
            id: `${modalId}-s2`,
            class: ' ',
            header: {
              modalTitleOverride: 'MODALS.RFP.SUCCESS.TITLE'
            },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.RFP.SUCCESS.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'measurementCalculations-form':
        return [
          {
            id: modalId,
            body: { componentType: MeasurementCalculationsModalComponent },
            footer: {
              saveBtnName: 'MODALS.MEASUREMENT_CALCULATIONS.SAVE_BUTTON',
              link: {
                text: 'MODALS.MEASUREMENT_CALCULATIONS.RESET',
                function: ModalFooterLinkFunction.RESET_FORM
              }
            }
          }
        ];
      case 'inviteMember-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: BranchInviteMemberModalComponent }
          },
          {
            id: `${modalId}-s2`,
            header: { modalTitleOverride: 'MODALS.INVITE_MEMBER_SUCCESS.HEADER_TITLE' },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.INVITE_MEMBER_SUCCESS.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'createSuperAdmin-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: BranchInviteMemberModalComponent }
          },
          {
            id: `${modalId}-s2`,
            header: { modalTitleOverride: 'MODALS.INVITE_MEMBER_SUCCESS.HEADER_TITLE' },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.INVITE_MEMBER_SUCCESS.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'deleteAccount-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: DeleteAccountModalComponent },
            footer: {
              saveBtnName: 'MODALS.DELETE_ACCOUNT.SAVE_BUTTON',
            }
          },
          {
            id: `${modalId}-s2`,
            header: { modalTitleOverride: 'MODALS.DELETE_ACCOUNT.SUCCESS_TITLE' },
            body: { componentType: SuccessModalComponent },
            footer: {
              saveBtnName: 'MODALS.DELETE_ACCOUNT.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'looseArticle-form':
        return [
          {
            id: `${modalId}-s1`,
            body: { componentType: LooseCategoriesModalComponent },
            footer: { hideFooter: true }
          },
          {
            id: `${modalId}-s2`,
            header: { modalTitleOverride: '{looseCategory}' },
            body: { componentType: AddLooseArticleToOfferModalComponent },
            footer: {
              saveBtnName: 'MODALS.LOOSE_ARTICLE_CREATE.SAVE_BUTTON',
              link: {
                function: ModalFooterLinkFunction.HIDE_LINK
              }
            }
          }
        ];
      case 'editFreeArticleInOffer-form':
      case 'editArticleInOffer-form':
        return [
          {
            id: modalId,
            body: { componentType: EditArticleInOfferModalComponent },
            footer: {
              saveBtnName: 'MODALS.ARTICLE_UPDATE.SAVE_TO_OFFER_BUTTON',
              showCancelBtn: false
            }
          }
        ];
      case 'branch-sftp-form':
        return [
          {
            id: modalId,
            body: { componentType: BranchSftpSettingsModalComponent }
          }
        ];
      default:
        return [] as ModalStep[];
    }
  }

  public transformStep(step: ModalStep, dataFromPreviousStep: ModalStepInputType): Observable<ModalStep> {
    const shouldTransformStepHeader = Boolean(step.header && step.header.title && step.header.title === '{customer}');

    const shouldTransformModalHeaderTitleFromPreviousStepData = Boolean(step.header && step.header.modalTitleOverride
      && step.header.modalTitleOverride === '{looseCategory}');

    const shouldTransformStepFooter = Boolean(step.id.includes('customer')
      || step.id.includes('objekt') || step.id.includes('contactPerson'));

    const shouldAddBranchNameToTitle = step.id === 'branch-form-s2' && step.header && step.header.modalTitleOverride;

    if (shouldTransformStepHeader) {
      return this.storeService.getCurrentCustomer().pipe(
        take(1),
        map(({ name }: Customer) => ({
          ...step,
          header: { title: name }
        }))
      );
    }

    if (shouldTransformStepFooter) {
      return combineLatest([
        this.storeService.getCurrentOffer(),
        this.storeService.getCurrentCustomer()
      ]).pipe(
        take(1),
        map(([{ customerId, objectId }, customer]: [OfferBase, Customer]) => {
          let removeCheckbox = false;
          const isContactPersonListStep = step.id === 'contactPerson-list'
            || step.id === 'customerAll-list-s2';
          const disableCreateLink = isContactPersonListStep
            && (customer?.contactPersons?.length ?? 0) >= MAX_CONTACT_PERSONS;
          const baseLink = step.footer && step.footer.link ? step.footer.link : undefined;
          const link = baseLink && disableCreateLink ? { ...baseLink, disabled: true } : baseLink;

          if (objectId && (step.id === 'customerAll-list-s2' || step.id === 'customer-form-s3'
            || step.id === 'contactPerson-list' || step.id === 'contactPerson-form')) {
            removeCheckbox = true;
          }

          if (customerId && (step.id === 'objekt-list' || step.id === 'objekt-form')) {
            removeCheckbox = true;
          }

          if (removeCheckbox) {
            return {
              ...step,
              footer: {
                checkbox: undefined,
                link
              }
            };
          }

          if (disableCreateLink && step.footer) {
            return {
              ...step,
              footer: {
                ...step.footer,
                link
              }
            };
          }

          return { ...step };
        })
      );
    }

    if (shouldAddBranchNameToTitle) {
      return this.storeService.getCurrentBranch()
        .pipe(
          concatMap((branch: Branch) => {
            const translationKey = (step.header && step.header.modalTitleOverride) || '';

            return this.translateService.get(translationKey, { branchName: branch.name });
          }),
          take(1),
          map((modalTitle) => ({
            ...step,
            header: { ...step.header, modalTitleOverride: modalTitle }
          }))
        );
    }

    if (shouldTransformModalHeaderTitleFromPreviousStepData && dataFromPreviousStep) {
      return of({
        ...step,
        header: {
          ...step.header,
          modalTitleOverride: this.translateService.instant((`LOOSE_ARTICLE_CATEGORIES.${dataFromPreviousStep}`))
        }
      });
    }

    return of(step);
  }

  public getModalData(
    group: ModalGroup,
    contentType: ModalContentType,
    isBasic = false,
    initialGroup?: ModalGroup | undefined
  ): Modal {
    const modalId = this.getModalId(group, contentType);
    const modalIdConst = constantCase(modalId.replace('form', 'create'));

    const baseModal: Modal = {
      id: modalId,
      title: `MODALS.${modalIdConst}.HEADER_TITLE`,
      groupId: `${group}-group`,
      steps: this.getSteps(modalId, initialGroup)
        .map((step: ModalStep) => (isBasic ? { ...step, footer: {} } : step))
    };
    switch (modalId) {
      case 'objekt-list':
      case 'objekt-form':
      case 'customer-list':
      case 'customer-form':
      case 'contactPerson-form':
      case 'individualCondition-form':
      case 'branch-form':
      case 'newCondition-form':
      case 'inviteMember-form':
      case 'looseArticle-form':
      case 'closedOffer-form':
        return baseModal;
      case 'send-success-form':
        return {
          ...baseModal,
          title: 'MODALS.RESEND_INVITATION.HEADER_TITLE'
        };
      case 'contactPerson-formUpdate':
        return {
          ...baseModal,
          title: 'MODALS.CONTACT_PERSON_UPDATE.HEADER_TITLE'
        };
      case 'createSuperAdmin-form':
        return {
          ...baseModal,
          title: 'Create SuperAdmin user'
        };
      case 'deleteAccount-form':
        return {
          ...baseModal,
          title: 'Delete user'
      };
      case 'contactPerson-list':
        return {
          ...baseModal,
          title: (initialGroup === 'contactPerson')
            ? 'MODALS.CONTACT_PERSON_LIST.HEADER_TITLE'
            : 'MODALS.CUSTOMER_LIST.HEADER_TITLE'
        };
      case 'customerAll-list': {
        return {
          ...baseModal,
          title: 'MODALS.CUSTOMER_LIST.HEADER_TITLE'
        };
      }
      case 'article-form':
        return {
          ...baseModal,
          title: 'MODALS.ARTICLE_CREATE.HEADER_TITLE'
        };
      case 'article-formUpdate':
        return {
          ...baseModal,
          title: 'MODALS.ARTICLE_UPDATE.HEADER_TITLE'
        };
      case 'freeArticle-form':
        return {
          ...baseModal,
          title: 'MODALS.FREE_ARTICLE_CREATE.HEADER_TITLE'
        };
      case 'freeArticle-formUpdate':
        return {
          ...baseModal,
          title: 'MODALS.FREE_ARTICLE_UPDATE.HEADER_TITLE'
        };
      case 'addArticleToOffer-form':
        return {
          ...baseModal,
          title: 'MODALS.ADD_ARTICLE_TO_OFFER.HEADER_TITLE'
        };
      case 'addFreeArticleToOffer-form':
        return {
          ...baseModal,
          title: 'MODALS.ADD_FREE_ARTICLE_TO_OFFER.HEADER_TITLE'
        };
      case 'addFavorite-form':
        return {
          ...baseModal,
          title: 'MODALS.ADD_FAVORITE.HEADER_TITLE'
        };
      case 'editFavorite-form':
        return {
          ...baseModal,
          title: 'MODALS.EDIT_FAVORITE.HEADER_TITLE'
        };
      case 'expirationDate-form':
        return {
          ...baseModal,
          title: 'MODALS.EXPIRATION_DATE.HEADER_TITLE'
        };
      case 'password-form':
        return {
          ...baseModal,
          title: 'MODALS.CHANGE_PASSWORD.HEADER_TITLE'
        };
      case 'measurementCalculations-form':
        return {
          ...baseModal,
          title: 'MODALS.MEASUREMENT_CALCULATIONS.HEADER_TITLE'
        };
      case 'offerPreview-form':
        return {
          ...baseModal,
          title: 'MODALS.OFFER_PREVIEW.HEADER_TITLE'
        };
      case 'offerPreviewSuccess-form':
        return {
          ...baseModal,
          title: 'MODALS.OFFER_PREVIEW.HEADER_TITLE'
        };
      case 'rfp-form':
        return {
          ...baseModal,
          title: 'MODALS.RFP.HEADER_TITLE'
        };
      case 'answeredRFPsPreview-form':
        return {
          ...baseModal,
          title: 'MODALS.ANSWERED_RFP_PREVIEW.HEADER_TITLE'
        };
      case 'editArticleInOffer-form':
        return {
          ...baseModal,
          title: 'MODALS.ARTICLE_UPDATE.HEADER_TITLE'
        };
      case 'editFreeArticleInOffer-form':
        return {
          ...baseModal,
          title: 'MODALS.FREE_ARTICLE_UPDATE.HEADER_TITLE'
        };
      case 'branch-sftp-form':
        return {
          ...baseModal,
          title: 'MODALS.BRANCH_SFTP_SETTINGS.HEADER_TITLE'
        };
      default:
        return {} as Modal;
    }
  }

  public getModalId(group: ModalGroup, contentType: ModalContentType): string {
    return `${group}-${contentType}`;
  }

  public getOfferModals(initialModalGroup: ModalGroup, isBasic = false): Modal[] {
    const groups: ModalGroup[] = ['objekt', 'customer', 'contactPerson', 'customerAll', 'expirationDate'];
    const contentTypes: ModalContentType[] = ['list', 'form'];

    return groups
      .map((group: ModalGroup) => contentTypes
        .map((contentType: ModalContentType) => this.getModalData(group, contentType, isBasic, initialModalGroup)))
      .reduce((acc: Modal[], item: Modal[]): Modal[] => [...acc, ...item], []);
  }
}
